import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import {
  selectQcSummaryPayload,
  selectIsStudySaved
} from '../../state/selectors';
import { FileMetaData, ReportContentItem } from '../../../shared/models/run.model';
import { Panel } from '../../../shared/models/assays.model';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { QcLot } from '../../../shared/models/qc-lot.model';
import { CalculationResult, Controls, QCControl, Vial } from '../../../shared/models/result.model';
import { adjustedColumnClass, adjustedColumnLabel, displayOrDash, formatComputedNumber } from '../../../shared/utils/helper';
import { getStatusClassFromResult } from '../../../shared/utils/assay-helper ';
import { ColumnSelection } from '../../../shared/models/dialog.model';
import {
  applyPrintColumnSelection,
  chunkColumnsForPrint,
  describePrintPage,
  PrintPageFit
} from '../../../shared/utils/print-column.util';

type QcSummaryColumn = {
  reportContentItems: ReportContentItem;
  valueAssigned: 'OriginalCalculate' | 'AdjustedCalculate';
  isMeanColumn: boolean;
};

@Component({
  selector: 'app-control-qcsummary',
  templateUrl: './control-qcsummary.component.html',
  styleUrl: './control-qcsummary.component.scss'
})
export class ControlQcsummaryComponent implements OnInit, AfterViewInit, OnDestroy {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  isPrintView = false;
  isPrintMode = false;
  forceRepaint = false;
  @ViewChild('qcSummaryScroll', { static: false }) qcSummaryScroll?: ElementRef<HTMLDivElement>;
  panelName = '';
  studyName = '';
  specVersion = '';
  appVersion = '';
  runType = '';
  testSpec = '';
  operatorName = '';
  refKitLot = '';
  /** Whether the adjusted columns hold a Calibrator Adjustment's Final run. */
  isFinal = false;

  get adjustedLabel(): string {
    return adjustedColumnLabel(this.isFinal);
  }
  patientFileMetadata: FileMetaData[] = [];
  panelSpecs: Panel | null = null;
  calLots: CalLot[] = [];
  qcLots: QcLot[] = [];
  reportContent: ReportContentItem[] = [];
  columnsAll: QcSummaryColumn[] = [];
  columns: QcSummaryColumn[] = [];
  tableColumns: ColumnSelection[] = [];
  selectedColumns: ColumnSelection[] = [];
  controlQcLevels: number[] = [];
  controlTestLevels: number[] = [];
  controlTestLots: string[] = [];
  readonly qcMetrics = ['BarCode', 'Mean', 'SD', 'N'] as const;
  readonly testMetrics = ['BarCode', 'Mean', 'SD', 'N'] as const;

  /**
   * The label column carries "Control Test - 1 / Level 2 / Mean", so it is wide; the data columns
   * hold a barcode or a number. Chunking is derived from these, not from a column count.
   */
  readonly printPageFit: PrintPageFit = { labelWidthMm: 62, columnWidthMm: 24 };

  /** Null until the operator narrows the print selection; see `applyPrintColumnSelection`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  private originalControlQcMap = new Map<string, QCControl>();
  private adjustedControlQcMap = new Map<string, QCControl>();
  private originalControlTestMap = new Map<string, QCControl[]>();
  private adjustedControlTestMap = new Map<string, QCControl[]>();
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  private destroy$ = new Subject<void>();

  trackByColumn = (_index: number, column: QcSummaryColumn) =>
    `${column.reportContentItems?.reportContentItemId ?? 'unknown'}-${column.valueAssigned}`;
  trackByPage = (index: number) => index;

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.isPrintView = this.router?.url?.includes('print');
    // The print route is print mode; there is no other reason to be on it. This used to be
    // switched on by the print menu opening over the page, which left the layout in screen mode
    // if the operator dismissed the menu.
    this.isPrintMode = this.isPrintView;
    this.store.select(selectQcSummaryPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({
        panelName,
        runType,
        testSpec,
        refKitLot,
        patientFileMetadata,
        panelSpecs,
        calLots,
        qcLots,
        reportContentResponse,
        calculationResult,
        calculationAdjustedResult,
        appState,
        isCalAdjustFinal
      }) => {
        this.isFinal = isCalAdjustFinal;
        this.panelName = panelName ?? '';
        this.studyName = appState?.selectedStudy?.studyName ?? '';
        this.specVersion = appState?.selectedStudy?.panelSpecs?.revisionNumber ?? '';
        this.appVersion = appState?.appVersion ?? '';
        this.runType = runType ?? '';
        this.testSpec = testSpec ?? '';
        this.refKitLot = refKitLot ?? '';
        this.patientFileMetadata = patientFileMetadata ?? [];
        this.panelSpecs = panelSpecs ?? null;
        this.calLots = calLots ?? [];
        this.qcLots = qcLots ?? [];
        this.operatorName = appState?.loggedInUser ?? '';

        const baseReportContent = reportContentResponse?.reportContentItems ?? [];
        this.reportContent = this.getEnrichedReportContent(
          baseReportContent,
          calculationResult,
          calculationAdjustedResult
        );
        this.columnsAll = this.buildColumns(this.reportContent, calculationAdjustedResult ?? null);
        this.columns = this.columnsAll;
        // Re-key any print selection onto the rebuilt columns. The store hands back every column
        // selected, so without this a rebuild — which the audit write on Print itself can cause —
        // would send the whole table to the printer however few columns were ticked.
        this.tableColumns = applyPrintColumnSelection(
          this.buildTableColumns(this.reportContent),
          this.printColumnSelection
        );
        this.selectedColumns = this.tableColumns;

        this.originalControlQcMap = this.buildControlQcMap(calculationResult?.controls);
        this.adjustedControlQcMap = this.buildControlQcMap(calculationAdjustedResult?.controls);
        this.originalControlTestMap = this.buildControlTestMap(calculationResult?.controls);
        this.adjustedControlTestMap = this.buildControlTestMap(calculationAdjustedResult?.controls);
        this.controlQcLevels = this.computeControlLevelNumbers();
        this.controlTestLevels = this.computeControlTestLevelNumbers();
        // Applied last: it narrows `columns` and orders the lots from the maps built just above.
        this.handleSelectColumns(this.tableColumns);
      });
  }

  ngAfterViewInit(): void {
    if (this.isPrintView) {
      requestAnimationFrame(() => {
        this.forceRepaint = true;
        setTimeout(() => {
          this.forceRepaint = false;
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayOrDash(value: unknown): string {
    return displayOrDash(value);
  }

  getStatusClassFromResult(status: string | undefined | null): string {
    return getStatusClassFromResult(status);
  }

  getUnitsForColumn(column: QcSummaryColumn): string {
    if (column.reportContentItems.units) {
      return column.reportContentItems.units;
    }
    const region = column.reportContentItems.region;
    const spec = this.panelSpecs?.Assay_Specs?.find(item => item.bead_region === region);
    return spec?.units ?? '-';
  }

  getRunDate(): string {
    const meta = this.patientFileMetadata?.[0];
    return meta?.date ?? '';
  }

  getCalibratorLot(): string {
    return this.calLots?.[0]?.calLotNumber ?? '';
  }

  getControlLot(): string {
    return this.qcLots?.[0]?.masterLotNumber ?? '';
  }

  getControlQcBarcode(column: QcSummaryColumn, levelNo: number): string {
    return this.displayOrDash(this.getControlQcVial(column, levelNo)?.barcode);
  }

  getControlQcMean(column: QcSummaryColumn, levelNo: number): string {
    return this.displayOrDash(this.getControlQcVial(column, levelNo)?.testMean);
  }

  getControlQcSd(column: QcSummaryColumn, levelNo: number): string {
    return this.displayOrDash(this.getControlQcVial(column, levelNo)?.repSD);
  }

  getControlQcCv(column: QcSummaryColumn, levelNo: number): string {
    return this.displayOrDash(this.getControlQcVial(column, levelNo)?.repCV);
  }

  getControlQcN(column: QcSummaryColumn, levelNo: number): string {
    return this.displayOrDash(this.getControlQcVial(column, levelNo)?.numReplicates);
  }

  getControlQcMetricValue(
    column: QcSummaryColumn,
    levelNo: number,
    metric: typeof this.qcMetrics[number]
  ): string {
    switch (metric) {
      case 'BarCode':
        return this.getControlQcBarcode(column, levelNo);
      case 'Mean':
        return this.getControlQcMean(column, levelNo);
      case 'SD':
        return levelNo == 1 ? this.getControlQcSd(column, levelNo) : this.getControlQcCv(column,  levelNo);
      case 'N':
        return this.getControlQcN(column, levelNo);
      default:
        return '-';
    }
  }

  getLevelMetricLabel(levelNo: number, metric: typeof this.qcMetrics[number]): string {
    if (metric === 'SD') {
      return levelNo === 1 ? 'SD' : '%CV';
    }
    return metric;
  }

  getControlQcCriteria(column: QcSummaryColumn, levelNo: number): string {
    const vial = this.getControlQcVial(column, levelNo);
    if (!vial || vial.sdSpecPass === undefined || vial.numSDsPass === undefined) {
      return '-';
    }
    const magnitude = Number(vial.sdSpecPass) * Number(vial.numSDsPass);
    if (!isFinite(magnitude)) {
      return '-';
    }
    if (vial.sdSpecType === '%') {
      return `${formatComputedNumber(magnitude * 100)}%`;
    }
    return formatComputedNumber(magnitude);
  }

  getControlQcRange(column: QcSummaryColumn, levelNo: number): string {
    const vial = this.getControlQcVial(column, levelNo);
    const lower = this.displayOrDash(vial?.lowerLimitPass);
    const upper = this.displayOrDash(vial?.upperLimitPass);
    if (lower === '-' && upper === '-') {
      return '-';
    }
    return `${lower} to ${upper}`;
  }

  getControlQcStatus(column: QcSummaryColumn, levelNo: number): string {
    const vial = this.getControlQcVial(column, levelNo);
    return this.formatStatusText(vial?.status);
  }

  getControlTestBarcode(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.displayOrDash(this.getControlTestVial(column, lot, levelNo)?.barcode);
  }

  getControlTestMean(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.displayOrDash(this.getControlTestVial(column, lot, levelNo)?.testMean);
  }

  getControlTestSd(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.displayOrDash(this.getControlTestVial(column, lot, levelNo)?.repSD);
  }

  getControlTestCv(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.displayOrDash(this.getControlTestVial(column, lot, levelNo)?.repCV);
  }

  getControlTestN(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.displayOrDash(this.getControlTestVial(column, lot, levelNo)?.numReplicates);
  }

  getControlTestMetricValue(
    column: QcSummaryColumn,
    lot: string,
    levelNo: number,
    metric: typeof this.testMetrics[number]
  ): string {
    switch (metric) {
      case 'BarCode':
        return this.getControlTestBarcode(column, lot, levelNo);
      case 'Mean':
        return this.getControlTestMean(column, lot, levelNo);
      case 'SD':
        return levelNo == 1 ? this.getControlTestSd(column, lot, levelNo) : this.getControlTestCv(column, lot, levelNo);
      case 'N':
        return this.getControlTestN(column, lot, levelNo);
      default:
        return '-';
    }
  }

  getLevelMetricLabelForTest(levelNo: number, metric: typeof this.testMetrics[number]): string {
    return this.getLevelMetricLabel(levelNo, metric);
  }

  getControlTestCriteriaAlert(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.getControlTestCriteriaValue(column, lot, levelNo, 'alert');
  }

  getControlTestCriteriaPass(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.getControlTestCriteriaValue(column, lot, levelNo, 'pass');
  }

  getControlTestRangeAlert(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.getControlTestRangeValue(column, lot, levelNo, 'alert');
  }

  getControlTestRangePass(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.getControlTestRangeValue(column, lot, levelNo, 'pass');
  }

  getControlTestStatusAlert(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.getControlTestStatusValue(column, lot, levelNo, 'alert');
  }

  getControlTestStatusPass(column: QcSummaryColumn, lot: string, levelNo: number): string {
    return this.getControlTestStatusValue(column, lot, levelNo, 'pass');
  }

  navigateToPrintView(): void {
    this.router.navigateByUrl('/(print:print/preview-qc-summary)');
  }

  handleSelectColumns(selectedColumns: ColumnSelection[]): void {
    this.selectedColumns = selectedColumns;
    this.columns = this.columnsAll.filter(column =>
      this.selectedColumns.some(selection => this.matchesSelection(selection, column.reportContentItems))
    );
    this.controlTestLots = this.computeControlTestLotOrder();
  }

  /** Columns chosen in the print preview bar; held so a rebuild cannot silently widen the print. */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumns(selectedColumns);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.runType,
      this.testSpec ? `Test Spec ${this.testSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : '',
      this.operatorName
    ].filter(Boolean).join(' · ');
  }

  get printPages(): QcSummaryColumn[][] {
    return chunkColumnsForPrint(this.columns, this.printPageFit);
  }

  /** "Columns 9-16 of 22 · page 2 of 3" over a chunk page; empty when the report was not split. */
  printPageCaption(pageIndex: number): string {
    return describePrintPage(pageIndex, this.printPages);
  }

  getColumnClasses(column: QcSummaryColumn): Record<string, boolean> {
    const isRef = !!this.refKitLot && (column.reportContentItems?.lot ?? '') === this.refKitLot;
    const isAdjusted = column.valueAssigned === 'AdjustedCalculate';
    return {
      'selected-kit-highlight': isRef,
      [adjustedColumnClass(this.isFinal)]: isAdjusted
    };
  }

  private buildTableColumns(reportContent: ReportContentItem[]): ColumnSelection[] {
    const selections = new Map<string, ColumnSelection>();
    reportContent.forEach(item => {
      const kitLot = item.lot;
      const instrumentId = item.runId;
      const beadRegion = item.region;
      const columnKey = this.buildColumnKey(kitLot, instrumentId, beadRegion);
      if (!selections.has(columnKey)) {
        selections.set(columnKey, {
          columnName: kitLot,
          columnKey,
          kitLot,
          instrumentId,
          beadRegion,
          isSelected: true
        });
      }
    });

    const orderedReportContent = [...reportContent].sort((a, b) => this.compareReportContentItems(a, b));
    const orderByKey = new Map<string, number>();
    orderedReportContent.forEach((item, index) => {
      const key = this.buildColumnKey(item.lot, item.runId, item.region);
      if (!orderByKey.has(key)) {
        orderByKey.set(key, index);
      }
    });

    return Array.from(selections.values()).sort((a, b) => {
      const aOrder = orderByKey.get(a.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderByKey.get(b.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }

  private matchesSelection(selection: ColumnSelection, reportContentItem: ReportContentItem): boolean {
    if (!selection.isSelected) {
      return false;
    }
    if (!selection.columnKey && !selection.instrumentId && selection.beadRegion === undefined) {
      return selection.columnName === reportContentItem.lot;
    }
    const key = this.buildColumnKey(reportContentItem.lot, reportContentItem.runId, reportContentItem.region);
    return (selection.columnKey ?? selection.columnName) === key;
  }

  private buildColumnKey(kitLot: string | undefined, instrumentId: string | undefined, beadRegion: number | undefined): string {
    return `${kitLot ?? ''}||${instrumentId ?? ''}||${beadRegion ?? ''}`;
  }

  private buildColumns(reportContent: ReportContentItem[], adjustedResult: CalculationResult | null): QcSummaryColumn[] {
    const base = reportContent.map(item => ({
      reportContentItems: item,
      valueAssigned: 'OriginalCalculate',
      isMeanColumn: this.isMeanReportItem(item.reportContentItemId)
    } as QcSummaryColumn));

    if (!adjustedResult) {
      return base;
    }

    const adjusted = reportContent.map(item => ({
      reportContentItems: item,
      valueAssigned: 'AdjustedCalculate',
      isMeanColumn: this.isMeanReportItem(item.reportContentItemId)
    } as QcSummaryColumn));

    // A Final run has no unadjusted baseline, so no unadjusted columns are shown.
    return (this.isFinal ? adjusted : [...base, ...adjusted]).sort((a, b) => this.compareColumns(a, b));
  }

  private compareColumns(left: QcSummaryColumn, right: QcSummaryColumn): number {
    const baseCompare = this.compareReportContentBase(left.reportContentItems, right.reportContentItems);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustCompare =
      (left.valueAssigned === 'AdjustedCalculate' ? 1 : 0) -
      (right.valueAssigned === 'AdjustedCalculate' ? 1 : 0);
    if (adjustCompare !== 0) {
      return adjustCompare;
    }

    const leftIsMean = left.isMeanColumn;
    const rightIsMean = right.isMeanColumn;
    if (leftIsMean !== rightIsMean) {
      return leftIsMean ? 1 : -1;
    }
    return (left.reportContentItems.runId ?? '').localeCompare(right.reportContentItems.runId ?? '');
  }

  private getEnrichedReportContent(
    baseReportContent: ReportContentItem[],
    calculationResult: CalculationResult | null | undefined,
    calculationAdjustedResult: CalculationResult | null | undefined
  ): ReportContentItem[] {
    if (!baseReportContent?.length) {
      return [];
    }
    const sorted = [...baseReportContent].sort((a, b) => this.compareReportContentItems(a, b));
    return this.appendMeanColumnsToReportContent(sorted, calculationResult, calculationAdjustedResult);
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    const baseCompare = this.compareReportContentBase(a, b);
    if (baseCompare !== 0) {
      return baseCompare;
    }
    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }
    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareReportContentBase(a: ReportContentItem, b: ReportContentItem): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (this.refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }

    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private appendMeanColumnsToReportContent(
    reportItems: ReportContentItem[],
    originalResult: CalculationResult | null | undefined,
    adjustedResult: CalculationResult | null | undefined
  ): ReportContentItem[] {
    if (!reportItems?.length) {
      return [];
    }

    if (reportItems.some(item => this.isMeanReportItem(item?.reportContentItemId))) {
      return [...reportItems];
    }

    const meanIds = this.collectAvailableMeanIds([originalResult, adjustedResult]);
    if (!meanIds.size) {
      return reportItems;
    }

    const useKitRegionIds = this.shouldUseKitRegionMeanIds(reportItems, meanIds);
    const insertedMeanIds = new Set<string>();
    const insertedRegions = new Set<number>();
    const result: ReportContentItem[] = [];

    for (let index = 0; index < reportItems.length; index++) {
      const item = reportItems[index];
      result.push(item);

      const regionValue = this.parseRegionValue(item.region);
      if (!Number.isFinite(regionValue)) {
        continue;
      }

      const next = reportItems[index + 1];
      const nextRegionValue = this.parseRegionValue(next?.region);
      const sameRegion = Number.isFinite(nextRegionValue) && regionValue === nextRegionValue;
      const sameKit = (item.lot ?? '') === (next?.lot ?? '');

      if (useKitRegionIds) {
        if (sameRegion && sameKit) {
          continue;
        }
        const meanId = this.buildMeanId(item, regionValue);
        if (!meanId || !meanIds.has(meanId) || insertedMeanIds.has(meanId)) {
          continue;
        }
        const meanColumn = this.createMeanReportContentItem(item, meanId);
        if (meanColumn) {
          insertedMeanIds.add(meanId);
          result.push(meanColumn);
        }
        continue;
      }

      if (sameRegion) {
        continue;
      }
      if (insertedRegions.has(regionValue)) {
        continue;
      }
      const legacyMeanId = this.buildLegacyMeanId(regionValue);
      if (!meanIds.has(legacyMeanId)) {
        continue;
      }
      const meanColumn = this.createMeanReportContentItem(item, legacyMeanId);
      if (meanColumn) {
        insertedRegions.add(regionValue);
        result.push(meanColumn);
      }
    }

    return result;
  }

  private collectAvailableMeanIds(calculations: (CalculationResult | null | undefined)[]): Set<string> {
    const ids = new Set<string>();
    calculations.forEach(calculation => {
      calculation?.runStatus?.forEach(status => {
        const reportContentItemId = status?.reportContentItemId;
        if (this.isMeanReportItem(reportContentItemId)) {
          ids.add(reportContentItemId as string);
        }
      });
    });
    return ids;
  }

  private shouldUseKitRegionMeanIds(reportItems: ReportContentItem[], meanIds: Set<string>): boolean {
    for (const item of reportItems) {
      const regionValue = this.parseRegionValue(item.region);
      if (!Number.isFinite(regionValue)) {
        continue;
      }
      const candidate = this.buildMeanId(item, regionValue);
      if (candidate && meanIds.has(candidate)) {
        return true;
      }
    }
    return false;
  }

  private buildMeanId(item: ReportContentItem, regionValue: number): string | null {
    const kit = `${item.lot ?? ''}`.trim();
    if (!kit) {
      return null;
    }
    return `Mean_${kit}${regionValue}`;
  }

  private buildLegacyMeanId(regionValue: number): string {
    return `Mean_${regionValue}`;
  }

  private createMeanReportContentItem(template: ReportContentItem, reportContentItemId: string): ReportContentItem | null {
    const meanColumn: ReportContentItem = {
      ...template,
      reportContentItemId,
      runId: 'Mean Values'
    };
    return meanColumn;
  }

  private isMeanReportItem(reportContentItemId: string | undefined): boolean {
    return !!reportContentItemId && reportContentItemId.startsWith('Mean_');
  }

  private buildControlQcMap(controls: Controls[] | null | undefined): Map<string, QCControl> {
    const map = new Map<string, QCControl>();
    controls?.forEach(control => {
      if (!control?.reportContentItemId || !Array.isArray(control?.qc)) {
        return;
      }
      const qcEntry = control.qc.find(qc => Array.isArray(qc?.vials) && qc.vials.length > 0);
      if (qcEntry) {
        map.set(control.reportContentItemId, qcEntry);
      }
    });
    return map;
  }

  private buildControlTestMap(controls: Controls[] | null | undefined): Map<string, QCControl[]> {
    const map = new Map<string, QCControl[]>();
    controls?.forEach(control => {
      if (!control?.reportContentItemId || !Array.isArray(control?.test)) {
        return;
      }
      const testEntries = control.test.filter(testControl => Array.isArray(testControl?.vials) && testControl.vials.length > 0);
      if (testEntries.length) {
        map.set(control.reportContentItemId, testEntries);
      }
    });
    return map;
  }

  private computeControlLevelNumbers(): number[] {
    const levelSet = new Set<number>();
    const collectLevels = (qcMap: Map<string, QCControl>) => {
      qcMap.forEach(control => {
        control?.vials?.forEach(vial => {
          const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
          if (!Number.isNaN(level)) {
            levelSet.add(level);
          }
        });
      });
    };
    collectLevels(this.originalControlQcMap);
    collectLevels(this.adjustedControlQcMap);
    return Array.from(levelSet).sort((a, b) => a - b);
  }

  private computeControlTestLevelNumbers(): number[] {
    const levelSet = new Set<number>();
    const collectLevels = (testMap: Map<string, QCControl[]>) => {
      testMap.forEach(testControls => {
        testControls?.forEach(control => {
          control?.vials?.forEach(vial => {
            const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
            if (!Number.isNaN(level)) {
              levelSet.add(level);
            }
          });
        });
      });
    };
    collectLevels(this.originalControlTestMap);
    collectLevels(this.adjustedControlTestMap);
    return Array.from(levelSet).sort((a, b) => a - b);
  }

  private computeControlTestLotOrder(): string[] {
    const seen = new Set<string>();
    const order: string[] = [];
    this.columns.forEach(column => {
      const entries = this.getControlTestEntries(column);
      entries.forEach(entry => {
        const lot = entry?.lotNumber;
        if (lot && !seen.has(lot)) {
          seen.add(lot);
          order.push(lot);
        }
      });
    });
    return order;
  }

  private getControlQcEntry(column: QcSummaryColumn): QCControl | undefined {
    const key = column?.reportContentItems?.reportContentItemId;
    if (!key) {
      return undefined;
    }
    const primaryMap = column.valueAssigned === 'AdjustedCalculate'
      ? this.adjustedControlQcMap
      : this.originalControlQcMap;
    return primaryMap.get(key) ?? this.originalControlQcMap.get(key) ?? this.adjustedControlQcMap.get(key);
  }

  private getControlQcVial(column: QcSummaryColumn, levelNo: number): Vial | undefined {
    return this.getControlQcEntry(column)?.vials?.find(vial => {
      const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
      return level === levelNo;
    });
  }

  private getControlTestEntries(column: QcSummaryColumn): QCControl[] {
    const key = column?.reportContentItems?.reportContentItemId;
    if (!key) {
      return [];
    }
    const preferredMap = column.valueAssigned === 'AdjustedCalculate'
      ? this.adjustedControlTestMap
      : this.originalControlTestMap;
    return preferredMap.get(key) ?? this.originalControlTestMap.get(key) ?? this.adjustedControlTestMap.get(key) ?? [];
  }

  private getControlTestControl(column: QcSummaryColumn, lot: string): QCControl | undefined {
    const entries = this.getControlTestEntries(column);
    if (!entries.length) {
      return undefined;
    }
    return entries.find(entry => entry?.lotNumber === lot) ?? entries[0];
  }

  private getControlTestVial(column: QcSummaryColumn, lot: string, levelNo: number): Vial | undefined {
    return this.getControlTestControl(column, lot)?.vials?.find(vial => {
      const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
      return level === levelNo;
    });
  }

  private getControlTestCriteriaValue(
    column: QcSummaryColumn,
    lot: string,
    levelNo: number,
    variant: 'alert' | 'pass'
  ): string {
    const vial = this.getControlTestVial(column, lot, levelNo);
    if (!vial) {
      return '-';
    }
    const spec = variant === 'alert' ? vial.sdSpecAlert : vial.sdSpecPass;
    const num = variant === 'alert' ? vial.numSDsAlert : vial.numSDsPass;
    if (spec === undefined || spec === null || num === undefined || num === null) {
      return '-';
    }
    const magnitude = Number(spec) * Number(num);
    if (!isFinite(magnitude)) {
      return '-';
    }
    if (vial.sdSpecType === '%') {
      return `${formatComputedNumber(magnitude * 100)}%`;
    }
    return formatComputedNumber(magnitude);
  }

  private getControlTestRangeValue(
    column: QcSummaryColumn,
    lot: string,
    levelNo: number,
    variant: 'alert' | 'pass'
  ): string {
    const vial = this.getControlTestVial(column, lot, levelNo);
    const lower = this.displayOrDash(variant === 'alert' ? vial?.lowerLimitAlert : vial?.lowerLimitPass);
    const upper = this.displayOrDash(variant === 'alert' ? vial?.upperLimitAlert : vial?.upperLimitPass);
    if (lower === '-' && upper === '-') {
      return '-';
    }
    return `${lower} to ${upper}`;
  }

  private getControlTestStatusValue(
    column: QcSummaryColumn,
    lot: string,
    levelNo: number,
    variant: 'alert' | 'pass'
  ): string {
    const vial = this.getControlTestVial(column, lot, levelNo);
    const status = variant === 'alert' ? vial?.alertStatus : vial?.status;
    return this.formatStatusText(status);
  }

  private formatStatusText(status: string | undefined | null): string {
    if (status === null || status === undefined) {
      return '-';
    }
    if (typeof status === 'number') {
      return `${status}`;
    }
    const formatted = this.displayOrDash(status);
    return formatted === '-' ? '-' : formatted.toUpperCase();
  }
}
